import { useEffect, useMemo, useRef } from "react";

// Type hint so TS doesn't complain when the CF script loads
declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible"; // (we never send "invisible")
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          "timeout-callback"?: () => void;
          action?: string; // e.g. "register_patient" or "register_specialist"
        }
      ) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

type Props = {
  siteKey: string;
  action?: string; // helps your backend’s risk rules, e.g. "register_patient"
  onToken: (token: string) => void;
  theme?: "light" | "dark" | "auto";
  // CF supports "normal" | "compact" | "flexible". We coerce anything else to "flexible".
  size?: "normal" | "compact" | "flexible" | string;
};

export default function Turnstile({
  siteKey,
  action,
  onToken,
  theme = "auto",
  size = "flexible",
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  // Ensure only supported sizes are sent to CF (avoid 106010/400 if "invisible" is passed)
  const safeSize = useMemo<"normal" | "compact" | "flexible">(() => {
    return (["normal", "compact", "flexible"] as const).includes(size as any)
      ? (size as any)
      : "flexible";
  }, [size]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let iv: ReturnType<typeof setInterval> | null = null;

    const render = () => {
      if (!window.turnstile) return; // script not ready yet
      if (widgetIdRef.current) return; // keep current widget; don't re-render on parent state updates

      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: siteKey,
        theme,
        size: safeSize,
        action,
        callback: (token) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(""),
        "error-callback": () => onTokenRef.current(""),
        "timeout-callback": () => onTokenRef.current(""),
      });
    };

    // If script already loaded
    if (window.turnstile) {
      render();
    } else {
      // If script is still loading, poll briefly
      let tries = 0;
      iv = setInterval(() => {
        tries++;
        if (window.turnstile || tries > 40) {
          if (iv) clearInterval(iv);
          iv = null;
          if (window.turnstile) render();
        }
      }, 100);
    }

    return () => {
      if (iv) clearInterval(iv);
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      el.innerHTML = "";
    };
  }, [siteKey, theme, safeSize, action]);

  return <div ref={ref} />;
}
